import type { StyleProp, TextStyle, ViewStyle } from 'react-native';

import colors from '@packages/core/styles/colors';
import React, { FC } from 'react';
import { StyleSheet, View } from 'react-native';

import { Typography, Icon, StsIconName } from './';

/** Props for {@link EmptyList}. */
interface EmptyListProps {
    /** The circle style handed to the hero icon. */
    heroContainerStyle?: StyleProp<ViewStyle>;
    iconName?: StsIconName;
    iconColor?: keyof typeof colors;
    /** The size handed to the icon when supplied. */
    iconSize?: number;
    title?: string;
    description?: string;
    children?: React.ReactNode;
    /** Styles applied after the default style of each part. */
    styles?: {
        /** The outer area of the message. */
        container?: StyleProp<ViewStyle>;
        /** The centered column of message parts. */
        content?: StyleProp<ViewStyle>;
        /** The text under the title. */
        description?: StyleProp<TextStyle>;
        /** The block around the icon. */
        icon?: StyleProp<ViewStyle>;
        /** The title of the message. */
        title?: StyleProp<TextStyle>;
    };
    /** The color of the title and description, graySix when absent. */
    textColor?: keyof typeof colors;
}

/**
 * An icon in a circle, a title, a description and an optional action.
 *
 * @param props - {@link EmptyListProps}
 * @returns the empty list message
 */
export const EmptyList: FC<EmptyListProps> = ({
    iconName,
    iconColor,
    title,
    description,
    children,
    heroContainerStyle,
    iconSize,
    styles: partStyles,
    textColor = 'graySix',
}) => {
    const sizeProps = iconSize === undefined ? {} : { size: iconSize };

    return (
        <View style={[styles.container, partStyles?.container]}>
            <View style={[styles.messageOuter, partStyles?.content]}>
                {iconName && (
                    <View style={[styles.iconOuter, partStyles?.icon]}>
                        <Icon
                            color={iconColor}
                            hero
                            heroContainerStyle={heroContainerStyle}
                            name={iconName}
                            {...sizeProps}
                        />
                    </View>
                )}

                {title && (
                    <Typography variant="h3" style={[styles.titleText, partStyles?.title]} color={textColor}>
                        {title}
                    </Typography>
                )}

                {description ? (
                    <Typography
                        variant="default"
                        color={textColor}
                        style={[styles.descriptionText, partStyles?.description]}
                    >
                        {description}
                    </Typography>
                ) : null}

                {children}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        paddingVertical: 56,
        paddingHorizontal: 24,
    },
    messageOuter: {
        alignItems: 'center',
    },
    iconOuter: {
        marginBottom: 32,
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    titleText: {
        marginBottom: 16,
        textAlign: 'center',
    },
    descriptionText: {
        textAlign: 'center',
    },
});
